// @flow
import * as React from 'react';

import { View } from 'reshaped';

import { selectCartProductOwner, selectCanChooseResellee } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { ContactBlockPlaceholder } from 'src/Reshaped/ContactBlockPlaceholder';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useOrganization } from 'src/hooks/useOrganization';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import { IncompatibleBilledMessage } from './components/IncompatibleBilledMessage';
import { CheckboxContact } from './componentsNDS/CheckboxContact';
import { ConfigurationButton } from './componentsNDS/ConfigurationButton';
import { ContactBlock } from './componentsNDS/ContactBlock';
import { ContactSelect } from './componentsNDS/ContactSelect';
import {
  selectCanChooseProductOwner,
  selectResellerOwnerId,
  selectShouldRedirectToResellerOwnerId,
} from './modules/selectors';

export function AccountChoice(): React.MixedElement {
  const { __ } = useTranslator();

  const {
    isReseller,
    updateSharingIdContext,
    sameOwnerCheckboxIsChecked: checked,
    setSameOwnerCheckboxIsChecked: setChecked,
    submitting,
  } = useCartSummaryContext();

  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const sharingId = currentOrg?.id;
  const productOwnerId = useAppSelector(selectCartProductOwner);

  const redirectToReseller = useAppSelector(
    selectShouldRedirectToResellerOwnerId,
  );
  const resellerOwner = useAppSelector(selectResellerOwnerId);

  const canChooseResellee = useAppSelector(
    state => currentOrg?.reseller && selectCanChooseResellee(state),
  );
  const canChooseProductOwner = useAppSelector(selectCanChooseProductOwner);

  const {
    isFetchingOrganization: isFetchingCartOwner,
    isLoadedOrganization: isLoadedCartOwner,
  } = useOrganization(sharingId);

  const {
    isFetchingOrganization: isFetchingProductOwner,
    isLoadedOrganization: isLoadedProductOwner,
  } = useOrganization(productOwnerId);

  React.useEffect(() => {
    if (redirectToReseller) {
      updateSharingIdContext({ newSharingId: resellerOwner });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showProductOwnerChoice = canChooseProductOwner || canChooseResellee;

  return (
    <IncompatibleBilledMessage>
      <View gap={12}>
        <View gap={4}>
          <ContactSelect
            title={__('Invoice contact')}
            type="invoiceSelectModal"
            disabled={submitting}
          >
            {isFetchingCartOwner && !isLoadedCartOwner ? (
              <ContactBlockPlaceholder />
            ) : (
              <ContactBlock />
            )}
          </ContactSelect>

          {showProductOwnerChoice && (
            <CheckboxContact
              checked={checked}
              setChecked={setChecked}
              disabled={submitting}
            />
          )}
        </View>

        {showProductOwnerChoice && (
          <>
            {!checked && (
              <View gap={4}>
                <ContactSelect
                  title={__('Owner')}
                  type={isReseller ? 'customerSelectModal' : 'ownerSelectModal'}
                  disabled={submitting}
                >
                  {isFetchingProductOwner && !isLoadedProductOwner ? (
                    <ContactBlockPlaceholder />
                  ) : (
                    <ContactBlock ownerType isCustomerBlock={isReseller} />
                  )}
                </ContactSelect>
              </View>
            )}

            <ConfigurationButton />
          </>
        )}
      </View>
    </IncompatibleBilledMessage>
  );
}
